import { parseBackup } from '@/helpers/backups';

const BACKUP_VERSION = '4';

const state = {
  isReady: false,
  isSignedIn: false,
  user: null,

  backups: [],
  nextPageToken: null,
  hasNextPage: false,
  isLoading: true,
};

const actions = {
  init({ commit, state }) {
    if (state.isReady) {
      return;
    }

    gapi.load('client:auth2', async () => {
      await gapi.client.init({
        apiKey: process.env.VUE_APP_GAPI_API_KEY,
        clientId: process.env.VUE_APP_GAPI_CLIENT_ID,
        discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
        scope: 'https://www.googleapis.com/auth/drive.appdata',
      });

      commit('setReady', true);

      gapi.auth2.getAuthInstance().isSignedIn.listen(signedIn => {
        commit('setSignedIn', signedIn);
      });
      commit('setSignedIn', gapi.auth2.getAuthInstance().isSignedIn.get());
    });
  },

  signIn() {
    gapi.auth2.getAuthInstance().signIn();
  },

  signOut() {
    gapi.auth2.getAuthInstance().signOut();
  },

  async load({ commit, dispatch }) {
    commit('setBackups', []);
    commit('setNextPageToken', null);
    commit('setHasNextPage', false);

    await dispatch('loadNextPage');
  },

  async loadNextPage({ commit, state }) {
    commit('setLoading', true);

    const response = await gapi.client.drive.files.list({
      spaces: 'appDataFolder',
      q: 'name contains "backups/"',
      fields: 'nextPageToken, files(id, name, appProperties, createdTime, size)',
      orderBy: 'createdTime desc',
      pageSize: 100,
      pageToken: state.nextPageToken,
    });

    commit('setNextPageToken', response.result.nextPageToken);
    commit('setHasNextPage', !!response.result.nextPageToken);

    const files = response.result.files.map(file => ({
      fileId: file.id,
      name: file.name.replace('backups/', ''),
      version: file.appProperties && file.appProperties.backupVersion
        ? file.appProperties.backupVersion : '0',
      created: file.createdTime,
      size: file.size,
    }));

    commit('setBackups', state.backups.concat(files));
    commit('setLoading', false);
  },

  async createBackup({ dispatch }, { name }) {
    const notes = await dispatch(
      'notes/getDataForBackup',
      null,
      { root: true },
    );
    const data = JSON.stringify(notes);

    const formData = new FormData();
    formData.append(
      'metadata',
      new Blob([
        JSON.stringify({
          name: `backups/${name}`,
          appProperties: {
            backupVersion: BACKUP_VERSION,
          },
          parents: ['appDataFolder'],
          mimeType: 'application/json',
        }),
      ], {
        type: 'application/json',
      }),
    );
    formData.append(
      'file',
      new Blob([
        data,
      ], {
        type: 'application/json',
      }),
    );

    await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${gapi.client.getToken().access_token}`,
      }),
      body: formData,
    });

    dispatch('load');
  },

  async restoreBackup({ dispatch }, { backup }) {
    const result = await gapi.client.drive.files.get({
      fileId: backup.fileId,
      alt: 'media',
    });

    const data = JSON.parse(result.body);
    const parsedBackupData = parseBackup(backup.version, data);

    dispatch(
      'notes/setDataFromBackup',
      parsedBackupData,
      { root: true },
    );
  },

  async deleteBackup({ dispatch }, { fileId }) {
    await gapi.client.drive.files.delete({
      fileId,
    });

    dispatch('load');
  },
};

const mutations = {
  setReady(state, ready) {
    state.isReady = ready;
  },

  setSignedIn(state, signedIn) {
    state.isSignedIn = signedIn;

    if (signedIn) {
      const profile = gapi.auth2.getAuthInstance().currentUser.get().getBasicProfile();
      state.user = {
        email: profile.getEmail(),
        name: profile.getName(),
        imageUrl: profile.getImageUrl(),
      };
    } else {
      state.user = null;
    }
  },

  setBackups(state, backups) {
    state.backups = backups;
  },

  setNextPageToken(state, nextPageToken) {
    state.nextPageToken = nextPageToken;
  },

  setHasNextPage(state, hasNextPage) {
    state.hasNextPage = hasNextPage;
  },

  setLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
